import Phaser from "phaser";

import { FACET_QUARTER_ART } from "../config/artAssets";

export type KingdomLoadingSceneCallbacks = {
  onReady?(): void;
};

type Spark = {
  object: Phaser.GameObjects.Graphics;
  xRatio: number;
  yRatio: number;
  phase: number;
  size: number;
};

export class KingdomLoadingScene extends Phaser.Scene {
  readonly #callbacks: KingdomLoadingSceneCallbacks;
  readonly #reducedMotion: boolean;
  readonly #renderScale: number;
  #width = 0;
  #height = 0;
  #baseY = 0;
  #baseScale = 1;
  #diamond!: Phaser.GameObjects.Image;
  #facetLight!: Phaser.GameObjects.Image;
  #sweepMask!: Phaser.GameObjects.Graphics;
  #sparks: Spark[] = [];

  constructor(callbacks: KingdomLoadingSceneCallbacks, reducedMotion: boolean, renderScale: number) {
    super({ key: "KingdomLoading" });
    this.#callbacks = callbacks;
    this.#reducedMotion = reducedMotion;
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.image(FACET_QUARTER_ART.diamond.base.key, FACET_QUARTER_ART.diamond.base.url);
  }

  create(): void {
    this.cameras.main.setBackgroundColor("rgba(0,0,0,0)");
    this.cameras.main.transparent = true;

    this.#diamond = this.add.image(0, 0, FACET_QUARTER_ART.diamond.base.key).setDepth(2);
    this.#facetLight = this.add
      .image(0, 0, FACET_QUARTER_ART.diamond.base.key)
      .setTint(0xeaffff)
      .setAlpha(0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(3);
    this.#sweepMask = this.make.graphics({ x: 0, y: 0 });
    this.#facetLight.setMask(this.#sweepMask.createGeometryMask());

    this.#sparks = [
      { xRatio: 0.31, yRatio: 0.35, phase: 0, size: 7 },
      { xRatio: 0.72, yRatio: 0.46, phase: 1.85, size: 5 },
      { xRatio: 0.53, yRatio: 0.73, phase: 3.6, size: 4 },
    ].map((spark) => ({ ...spark, object: this.#createSpark(spark.size) }));

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#callbacks.onReady?.();
  }

  override update(time: number): void {
    if (this.#width === 0 || this.#height === 0) return;
    const motion = this.#reducedMotion ? 0 : Math.sin(time / 1_080) * 4;
    const breathing = this.#reducedMotion ? 1 : 1 + Math.sin(time / 1_470) * 0.006;
    const y = this.#baseY + motion;
    const scale = this.#baseScale * breathing;
    this.#diamond.setPosition(this.#width / 2, y).setScale(scale);
    this.#facetLight.setPosition(this.#width / 2, y).setScale(scale);

    const sweep = this.#reducedMotion ? 0.48 : (time % 2_600) / 2_600;
    const sweepX = -this.#width * 0.28 + sweep * this.#width * 1.56;
    this.#sweepMask.clear();
    this.#sweepMask.fillStyle(0xffffff, 1);
    this.#sweepMask.save();
    this.#sweepMask.translateCanvas(sweepX, this.#height / 2);
    this.#sweepMask.rotateCanvas(-0.24);
    this.#sweepMask.fillRect(-11, -this.#height, 22, this.#height * 2);
    this.#sweepMask.restore();

    this.#sparks.forEach((spark) => {
      const wave = this.#reducedMotion ? 0.32 : Math.max(0, Math.sin(time / 470 + spark.phase));
      spark.object
        .setPosition(this.#width * spark.xRatio, y + (spark.yRatio - 0.52) * 126)
        .setScale(0.72 + wave * 0.42)
        .setAlpha(0.16 + wave * 0.74);
    });
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0) return;
    this.#width = width;
    this.#height = height;
    this.#baseY = height * 0.48;
    this.#baseScale = Math.min(
      (width * 0.62) / FACET_QUARTER_ART.diamond.base.width,
      (height * 0.66) / FACET_QUARTER_ART.diamond.base.height,
    );
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);
  }

  #createSpark(size: number): Phaser.GameObjects.Graphics {
    const spark = this.add.graphics().setDepth(4).setBlendMode(Phaser.BlendModes.ADD);
    spark.lineStyle(1.2, 0xffefbd, 0.95);
    spark.beginPath();
    spark.moveTo(-size, 0);
    spark.lineTo(size, 0);
    spark.moveTo(0, -size);
    spark.lineTo(0, size);
    spark.strokePath();
    spark.lineStyle(0.8, 0xa6fff4, 0.76);
    spark.beginPath();
    spark.moveTo(-size * 0.45, -size * 0.45);
    spark.lineTo(size * 0.45, size * 0.45);
    spark.moveTo(size * 0.45, -size * 0.45);
    spark.lineTo(-size * 0.45, size * 0.45);
    spark.strokePath();
    return spark;
  }
}
